import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  Badge,
  Modal,
} from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import api from "../api";
import LocationSelect from "./LocationSelect";
import { FaMapMarkerAlt } from "react-icons/fa";

const WORKER_CATEGORIES = [
  { value: "plumber", label: "Plumber" },
  { value: "electrician", label: "Electrician" },
  { value: "carpenter", label: "Carpenter" },
  { value: "painter", label: "Painter" },
  { value: "cleaner", label: "Cleaner" },
  { value: "gardener", label: "Gardener" },
  { value: "security-guard", label: "Security Guard" },
  { value: "pandit", label: "Pandit" },
  { value: "doctor", label: "Doctor" },
  { value: "kabadiwala", label: "Kabadiwala" },
  { value: "nurse", label: "Nurse" },
  { value: "chef", label: "Chef" },
  { value: "maid", label: "Maid" },
  { value: "babysitter", label: "Babysitter" },
  { value: "mechanic", label: "Mechanic" },
  { value: "tution-teacher", label: "Tuition Teacher" },
  { value: "driver", label: "Driver" },
  { value: "other", label: "Other" },
];

export default function WorkerRegistration() {
  const { user } = useContext(AuthContext);
  const [category, setCategory] = useState("");
  const [experience, setExperience] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [description, setDescription] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [myWorkers, setMyWorkers] = useState([]);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [myMessage, setMyMessage] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const createEmptyEditForm = () => ({
    category: "",
    experience: "",
    hourlyRate: "",
    description: "",
    state: "",
    city: "",
    isAvailable: true,
  });
  const [editForm, setEditForm] = useState(createEmptyEditForm);
  const [editError, setEditError] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  const formatCategory = (value) =>
    value
      ? value
          .split("-")
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ")
      : "";

  const fetchMyWorkers = useCallback(async () => {
    const userId = user?._id || user?.id;

    if (!userId) {
      setMyWorkers([]);
      setMyError("");
      setMyMessage("");
      setMyLoading(false);
      return;
    }

    setMyLoading(true);
    setMyError("");
    setMyMessage("");
    try {
      const res = await api.get(`/workers/user/${userId}`);
      if (res.data?.success) {
        setMyWorkers(res.data.workers || []);
      } else {
        setMyError(res.data?.message || "Failed to load your worker listings.");
      }
    } catch (err) {
      console.error("Fetch my workers error:", err);
      setMyError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to load your worker listings."
      );
    } finally {
      setMyLoading(false);
    }
  }, [user]);

  const handleRemoveWorker = async (worker) => {
    if (!worker?._id) {
      return;
    }

    const currentUserId = user?._id || user?.id;
    if (!currentUserId) {
      setMyError("Please login to manage your worker listings.");
      return;
    }

    const categoryLabel = formatCategory(worker.category) || "worker";
    const confirmed = window.confirm(`Remove your ${categoryLabel} listing?`);

    if (!confirmed) {
      return;
    }

    setMyError("");
    setEditError("");
    setMyMessage("");
    setMessage("");
    setRemovingId(worker._id);

    try {
      const res = await api.delete(`/workers/${worker._id}`, {
        data: { userId: currentUserId },
      });

      if (res.data?.success) {
        setMyWorkers((prev) =>
          prev.filter((item) => String(item._id) !== String(worker._id))
        );
        setMyMessage(
          res.data?.message || "Worker listing removed successfully."
        );
      } else {
        throw new Error(
          res.data?.message || "Failed to remove worker listing."
        );
      }
    } catch (err) {
      console.error("Remove worker listing error:", err);
      setMyError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to remove worker listing."
      );
    } finally {
      setRemovingId("");
    }
  };

  const openEditModal = (worker) => {
    if (!worker) {
      return;
    }

    setEditingWorker(worker);
    setEditForm({
      category: worker.category || "",
      experience:
        worker.experience !== undefined && worker.experience !== null
          ? String(worker.experience)
          : "",
      hourlyRate:
        worker.hourlyRate !== undefined && worker.hourlyRate !== null
          ? String(worker.hourlyRate)
          : "",
      description: worker.description || "",
      state: worker.state || "",
      city: worker.city || "",
      isAvailable:
        typeof worker.isAvailable === "boolean" ? worker.isAvailable : true,
    });
    setEditError("");
    setMyMessage("");
    setShowEditModal(true);
  };

  const closeEditModal = (force = false) => {
    if (editSubmitting && !force) {
      return;
    }

    setShowEditModal(false);
    setEditingWorker(null);
    setEditError("");
    setEditForm(createEmptyEditForm());
  };

  const handleEditFieldChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditAvailabilityChange = (event) => {
    setEditForm((prev) => ({ ...prev, isAvailable: event.target.checked }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();

    if (!editingWorker?._id) {
      setEditError("No worker listing selected to edit.");
      return;
    }

    const currentUserId = user?._id || user?.id;
    if (!currentUserId) {
      setEditError("Please login to edit your worker listing.");
      return;
    }

    if (!editForm.category) {
      setEditError("Please select a category.");
      return;
    }

    const numericExperience = Number(editForm.experience);
    if (Number.isNaN(numericExperience) || numericExperience < 0) {
      setEditError("Experience must be a non-negative number.");
      return;
    }

    const numericHourlyRate = Number(editForm.hourlyRate);
    if (Number.isNaN(numericHourlyRate) || numericHourlyRate < 0) {
      setEditError("Hourly rate must be a non-negative number.");
      return;
    }

    const trimmedDescription = (editForm.description || "").trim();
    if (trimmedDescription.length < 50 || trimmedDescription.length > 500) {
      setEditError("Description must be between 50 and 500 characters.");
      return;
    }

    const trimmedState = (editForm.state || "").trim();
    const trimmedCity = (editForm.city || "").trim();

    if (!trimmedState || !trimmedCity) {
      setEditError("Please select both state and city.");
      return;
    }

    setEditError("");
    setMyError("");
    setEditSubmitting(true);

    try {
      const res = await api.patch(`/workers/${editingWorker._id}`, {
        userId: currentUserId,
        category: editForm.category,
        experience: numericExperience,
        hourlyRate: numericHourlyRate,
        description: trimmedDescription,
        state: trimmedState,
        city: trimmedCity,
        isAvailable: Boolean(editForm.isAvailable),
      });

      if (res.data?.success && res.data?.worker) {
        setMyWorkers((prev) =>
          prev.map((item) =>
            String(item._id) === String(editingWorker._id)
              ? res.data.worker
              : item
          )
        );
        setMyMessage(
          res.data?.message || "Worker listing updated successfully."
        );
        setMessage("");
        setError("");
        closeEditModal(true);
      } else {
        setEditError(res.data?.message || "Failed to update worker listing.");
      }
    } catch (err) {
      console.error("Worker listing update error:", err);
      setEditError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to update worker listing."
      );
    } finally {
      setEditSubmitting(false);
    }
  };

  // Initialize location from user profile if available
  useEffect(() => {
    if (user?.state) setState(user.state);
    if (user?.city) setCity(user.city);
  }, [user]);

  useEffect(() => {
    fetchMyWorkers();
  }, [fetchMyWorkers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setMyError("");
    setMyMessage("");
    setLoading(true);

    // Check if user is logged in
    if (!user) {
      setError("Please login first to register as a worker");
      setLoading(false);
      return;
    }

    // Get user ID (support both _id and id)
    const userId = user._id || user.id;
    if (!userId) {
      setError("User ID not found. Please login again.");
      setLoading(false);
      return;
    }

    // Validate form fields
    if (!category) {
      setError("Please select a category");
      setLoading(false);
      return;
    }
    const numericExperience = Number(experience);
    if (Number.isNaN(numericExperience) || numericExperience < 0) {
      setError("Please enter a valid experience in years");
      setLoading(false);
      return;
    }
    const numericHourlyRate = Number(hourlyRate);
    if (Number.isNaN(numericHourlyRate) || numericHourlyRate < 0) {
      setError("Please enter a valid hourly rate");
      setLoading(false);
      return;
    }
    if (!description || description.length < 50 || description.length > 500) {
      setError("Description must be between 50 and 500 characters");
      setLoading(false);
      return;
    }
    if (!state || !city) {
      setError("Please select both state and city");
      setLoading(false);
      return;
    }

    try {
      console.log("Submitting worker registration with:", {
        userId,
        userPhone: user?.phone,
        category,
        experience: numericExperience,
        hourlyRate: numericHourlyRate,
        state,
        city,
      });

      // hit API route path (baseURL already includes /api)
      const res = await api.post("/workers/register", {
        userId: userId,
        phone: user?.phone, // Include phone as fallback
        category,
        experience: numericExperience,
        hourlyRate: numericHourlyRate,
        description,
        state: state,
        city: city,
      });

      if (res.data.success) {
        setMessage(
          res.data.message ||
            "Successfully registered as a worker! Our team will call you to schedule a short skill verification (videocall or in-person). Once verified, your profile will show a Verified badge to help customers trust your skills."
        );
        setMyError("");
        // Reset form
        setCategory("");
        setExperience("");
        setHourlyRate("");
        setDescription("");
        await fetchMyWorkers();
      } else {
        setError(
          res.data.message || res.data.error || "Failed to register as worker"
        );
      }
    } catch (err) {
      console.error("Worker registration error:", err.response || err);
      const errMsg =
        err.response?.data?.message || err.response?.data?.error || err.message;
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // Debug: Log user object to help diagnose issues
  React.useEffect(() => {
    if (user) {
      console.log("Current user object:", user);
      console.log("User ID (_id):", user._id);
      console.log("User ID (id):", user.id);
    } else {
      console.warn("No user found in context");
    }
  }, [user]);

  return (
    <div className="worker-registration">
      <h3>Register as a Worker</h3>
      {!user && (
        <Alert variant="warning">
          Please login first to register as a worker.
        </Alert>
      )}
      <Alert variant="info" className="mb-4">
        <strong>Skill verification:</strong> After you register, our team will call you to schedule a short skill check (videocall or in-person). Once verified, your profile will show a <strong>Verified</strong> badge so customers can trust your skills.
      </Alert>
      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Category</Form.Label>
          <Form.Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
            isInvalid={error && !category}
          >
            <option value="">Choose a category</option>
            {WORKER_CATEGORIES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            Please select a category
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Years of Experience</Form.Label>
          <Form.Control
            type="number"
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            min="0"
            required
            isInvalid={error && (!experience || experience < 0)}
          />
          <Form.Control.Feedback type="invalid">
            Please enter a valid experience in years
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Hourly Rate (₹)</Form.Label>
          <Form.Control
            type="number"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            min="0"
            required
            isInvalid={error && (!hourlyRate || hourlyRate < 0)}
          />
          <Form.Control.Feedback type="invalid">
            Please enter a valid hourly rate
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your skills and experience (50-500 characters)"
            required
            minLength={50}
            maxLength={500}
            isInvalid={
              error &&
              (!description ||
                description.length < 50 ||
                description.length > 500)
            }
          />
          <Form.Text className="text-muted">
            {description.length}/500 characters
          </Form.Text>
          <Form.Control.Feedback type="invalid">
            Description must be between 50 and 500 characters
          </Form.Control.Feedback>
        </Form.Group>

        <div className="mb-3">
          <Form.Label className="fw-semibold">
            <FaMapMarkerAlt className="me-2 text-primary" /> Location
          </Form.Label>
          <div className="p-3 bg-light rounded-3 border">
            <LocationSelect
              selectedState={state}
              selectedCity={city}
              onStateChange={setState}
              onCityChange={setCity}
              stateLabel="State"
              cityLabel="City"
              stateRequired={true}
              cityRequired={true}
            />
            {user?.state && user?.city && !state && !city && (
              <div className="text-muted small mt-2">
                <em>
                  Your profile location: {user.city}, {user.state}
                </em>
              </div>
            )}
          </div>
        </div>

        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? "Registering..." : "Register as Worker"}
        </Button>
      </Form>

      <hr className="my-5" />

      <div className="my-worker-listings">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h4 className="mb-0">My Worker Listings</h4>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={fetchMyWorkers}
            disabled={myLoading}
          >
            {myLoading ? (
              <span className="d-inline-flex align-items-center">
                Refreshing
                <Spinner
                  animation="border"
                  size="sm"
                  className="ms-2"
                  role="status"
                />
              </span>
            ) : (
              "Refresh"
            )}
          </Button>
        </div>

        {myError && <Alert variant="danger">{myError}</Alert>}
        {myMessage && <Alert variant="success">{myMessage}</Alert>}

        {myLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : myWorkers.length === 0 ? (
          <Alert variant="info">
            You have not posted any worker listings yet.
          </Alert>
        ) : (
          <Row className="g-4">
            {myWorkers.map((worker) => {
              const descriptionPreview =
                worker.description && worker.description.length > 160
                  ? `${worker.description.slice(0, 160)}...`
                  : worker.description;

              return (
                <Col key={worker._id} md={6} lg={4}>
                  <Card className="h-100 shadow-sm border-0">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="mb-1 text-capitalize">
                            {formatCategory(worker.category)}
                          </h5>
                          <Badge
                            bg={worker.isAvailable ? "success" : "secondary"}
                          >
                            {worker.isAvailable ? "Available" : "Unavailable"}
                          </Badge>
                        </div>
                        <div className="text-end small text-muted">
                          <div>Experience: {worker.experience} yrs</div>
                          <div>Rate: ₹{worker.hourlyRate}/hr</div>
                        </div>
                      </div>

                      <div className="mb-2 text-secondary small">
                        <strong>Location:</strong> {worker.city}, {worker.state}
                      </div>

                      <div className="mb-3 text-secondary small">
                        <strong>Description:</strong>
                        <div className="mt-1">
                          {descriptionPreview || "No description provided."}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEditModal(worker)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={removingId === worker._id}
                          onClick={() => handleRemoveWorker(worker)}
                        >
                          {removingId === worker._id ? (
                            <span className="d-inline-flex align-items-center">
                              Removing
                              <Spinner
                                animation="border"
                                size="sm"
                                className="ms-2"
                                role="status"
                              />
                            </span>
                          ) : (
                            "Remove"
                          )}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </div>

      <Modal
        show={showEditModal}
        onHide={() => closeEditModal()}
        centered
        backdrop={editSubmitting ? "static" : true}
        keyboard={!editSubmitting}
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton={!editSubmitting}>
            <Modal.Title>Edit Worker Listing</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editError && <Alert variant="danger">{editError}</Alert>}

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                name="category"
                value={editForm.category}
                onChange={handleEditFieldChange}
                required
              >
                <option value="">Choose a category</option>
                {WORKER_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Years of Experience</Form.Label>
              <Form.Control
                type="number"
                name="experience"
                value={editForm.experience}
                min="0"
                onChange={handleEditFieldChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Hourly Rate (₹)</Form.Label>
              <Form.Control
                type="number"
                name="hourlyRate"
                value={editForm.hourlyRate}
                min="0"
                onChange={handleEditFieldChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={editForm.description}
                onChange={handleEditFieldChange}
                minLength={50}
                maxLength={500}
                required
              />
              <Form.Text className="text-muted">
                {editForm.description.length}/500 characters
              </Form.Text>
            </Form.Group>

            <div className="mb-3">
              <Form.Label className="fw-semibold">
                <FaMapMarkerAlt className="me-2 text-primary" /> Location
              </Form.Label>
              <div className="p-3 bg-light rounded-3 border">
                <LocationSelect
                  selectedState={editForm.state}
                  selectedCity={editForm.city}
                  onStateChange={(value) =>
                    setEditForm((prev) => ({ ...prev, state: value }))
                  }
                  onCityChange={(value) =>
                    setEditForm((prev) => ({ ...prev, city: value }))
                  }
                  stateLabel="State"
                  cityLabel="City"
                  stateRequired={true}
                  cityRequired={true}
                />
              </div>
            </div>

            <Form.Group className="mb-0">
              <Form.Check
                type="switch"
                id="edit-worker-availability"
                label="Available for work"
                checked={Boolean(editForm.isAvailable)}
                onChange={handleEditAvailabilityChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => closeEditModal(true)}
              disabled={editSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={editSubmitting}>
              {editSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
